import useSWR from "swr";
import { useAuthContext } from "src/auth/hooks";
import { officeSourceId, officeSourcePage } from "./office-source";
import axios, { fetcher } from "src/utils/axios";
import type {
  FinanceMode,
  FinanceOptions,
  FinancePage,
  FederationFee,
} from "src/types/federation-finance";

export const financeBase = (id: number) =>
  `/providers/${id}/federation/finance`;
export function usePlayerFinanceNavigation(userId?: number) {
  const result = useSWR<{ has_federation_membership: boolean }>(
    userId ? ["/providers/federation-finance/player-navigation", userId] : null,
    ([url]: [string, number]) => fetcher(url),
    { keepPreviousData: false },
  );
  return {
    ...result,
    hasFederationMembership:
      Boolean(userId) &&
      !result.error &&
      result.data?.has_federation_membership === true,
  };
}

export function useFinanceCapabilities(id: number, enabled: boolean) {
  return useSWR<{
    allowed_actions: string[];
    currency?: string;
    can_read_fees?: boolean;
  }>(id > 0 && enabled ? `${financeBase(id)}/capabilities` : null, fetcher, {
    keepPreviousData: false,
  });
}
export function useFinanceFees(id: number, enabled: boolean) {
  return useSWR<{ items: FederationFee[] }>(
    id > 0 && enabled ? `${financeBase(id)}/fees` : null,
    fetcher,
    { keepPreviousData: false },
  );
}
export function useFinanceObligations(
  mode: FinanceMode,
  id: number,
  enabled: boolean,
  offset = 0,
  state = "",
  purpose = "",
  filters: { payer?: string; from_date?: string; to_date?: string } = {},
) {
  const { user } = useAuthContext();
  const source = mode === "federation" ? officeSourceId("finance", id) : 0;
  const url =
    mode === "player"
      ? "/providers/federation-finance/my-obligations"
      : mode === "association"
      ? `/providers/${id}/federation-obligations`
      : `${financeBase(id)}/obligations${source ? `/${source}#actor-${user?.id}` : ""}`;
  return useSWR<FinancePage>(
    enabled && (mode === "player" || id > 0)
      ? [
          url,
          {
            params: {
              offset,
              limit: 20,
              ...(state && { state }),
              ...(purpose && { purpose }),
              ...Object.fromEntries(
                Object.entries(filters).filter(([, value]) => Boolean(value)),
              ),
            },
          },
        ]
      : null,
    source ? officeSourcePage : fetcher,
    { keepPreviousData: false },
  );
}
export async function financeOptions(id: number, email = "") {
  return (
    await axios.get<FinanceOptions>(`${financeBase(id)}/options`, {
      params: { email },
    })
  ).data;
}
export async function financeCommand<T = unknown>(
  url: string,
  payload: Record<string, unknown>,
) {
  return (await axios.post<T>(url, payload)).data;
}
export function financeErrorKey(error: unknown) {
  if (
    ((error as { detail?: unknown })?.detail ??
      (error as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail) === "federation_debt_action_blocked"
  )
    return "debt_action_blocked";
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401 || status === 403) return "finance_denied";
  if (status === 503) return "finance_setup_required";
  if (status === 409) return "finance_conflict";
  if (status === 422) return "finance_invalid";
  return "finance_error";
}

export async function financeInvoice(obligationId: number, linkId: number) {
  return (
    await axios.get<
      import("src/types/federation-finance").FinanceInvoiceDetail
    >(
      `/providers/federation-finance/obligations/${obligationId}/invoices/${linkId}`,
    )
  ).data;
}
